const CACHE_KEY = 'minute7_features_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export const FeaturesCache = {
    /**
     * Save features to localStorage with timestamp
     * @param {Object} features - The features object to cache
     */
    save: features => {
        try {
            const cacheData = {
                features,
                timestamp: Date.now(),
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
            console.log('Features cached to localStorage');
        } catch (error) {
            console.warn('Failed to save features to localStorage:', error);
        }
    },

    /**
     * Load features from localStorage if not expired
     * @returns {Object|null} - Features object or null if not found/expired
     */
    load: () => {
        try {
            const cachedData = localStorage.getItem(CACHE_KEY);
            if (!cachedData) {
                return null;
            }

            const { features, timestamp } = JSON.parse(cachedData);
            const now = Date.now();
            const age = now - timestamp;

            // Check if cache is expired (older than CACHE_DURATION — 24 hours)
            if (age > CACHE_DURATION) {
                console.log('Features cache expired, removing...');
                FeaturesCache.clear();
                return null;
            }

            console.log(`Features loaded from cache (age: ${Math.round(age / 1000)}s)`);
            return features;
        } catch (error) {
            console.warn('Failed to load features from localStorage:', error);
            FeaturesCache.clear(); // Clear corrupted cache
            return null;
        }
    },

    /**
     * Clear features cache from localStorage
     */
    clear: () => {
        try {
            localStorage.removeItem(CACHE_KEY);
            console.log('Features cache cleared');
        } catch (error) {
            console.warn('Failed to clear features cache:', error);
        }
    },

    /**
     * Check if cache exists and is valid
     * @returns {boolean} - True if cache exists and is not expired
     */
    isValid: () => {
        try {
            const cachedData = localStorage.getItem(CACHE_KEY);
            if (!cachedData) {
                return false;
            }

            const { timestamp } = JSON.parse(cachedData);
            const age = Date.now() - timestamp;
            return age <= CACHE_DURATION;
        } catch (error) {
            return false;
        }
    },

    /**
     * Get cache info for debugging
     * @returns {Object} - Cache info including age and validity
     */
    getInfo: () => {
        try {
            const cachedData = localStorage.getItem(CACHE_KEY);
            if (!cachedData) {
                return { exists: false };
            }

            const { timestamp } = JSON.parse(cachedData);
            const age = Date.now() - timestamp;
            const isValid = age <= CACHE_DURATION;

            return {
                exists: true,
                age: Math.round(age / 1000), // age in seconds
                isValid,
                expiresIn: isValid ? Math.round((CACHE_DURATION - age) / 1000) : 0,
            };
        } catch (error) {
            return { exists: false, error: error.message };
        }
    },
};
